import { useEffect, useMemo } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Apple, Play, ArrowLeft, Sparkles } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { useSEO } from "@/hooks/useSEO";
import { APP_LINKS } from "@/constants/appLinks";

// UA detection — bots are always excluded from redirect so search / social
// preview crawlers get the static preview page (with correct OG meta).
const BOT_RE = /(bot|crawler|spider|slurp|facebookexternalhit|LinkedInBot|Twitterbot|Slackbot|WhatsApp|TelegramBot|Applebot|Discordbot|Embedly|Pinterest|redditbot|Vkshare|Iframely|LinkPreview|SkypeUriPreview)/i;

const detect = () => {
  if (typeof navigator === "undefined") return { platform: "desktop", isBot: false };
  const ua = navigator.userAgent || "";
  const isBot = BOT_RE.test(ua);
  if (isBot) return { platform: "desktop", isBot: true };
  const iOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && "ontouchend" in document); // iPadOS 13+
  if (iOS) return { platform: "ios", isBot: false };
  if (/Android/i.test(ua)) return { platform: "android", isBot: false };
  return { platform: "desktop", isBot: false };
};

// Android Intent URI that forces the Google Play Store app (com.android.vending)
// so OEM stores (Vivo/Oppo/Realme/Xiaomi/Samsung Galaxy Store etc.) can't intercept it.
// If Play Store isn't installed, browsers follow the HTTPS fallback.
const ANDROID_PACKAGE = "app.emergent.indiareels1f783207d";
const buildPlayIntent = () =>
  `intent://details?id=${ANDROID_PACKAGE}` +
  `#Intent;scheme=market;package=com.android.vending;` +
  `S.browser_fallback_url=${encodeURIComponent(APP_LINKS.googlePlay)};end`;

// Kind is inferred from the route so a single component handles all deep links.
export default function SmartRedirect({ kind }) {
  const params = useParams();
  const location = useLocation();
  const id = params.id || params.username || "";
  const path = location.pathname + location.search;

  const { platform, isBot } = useMemo(detect, []);
  const label = kind === "profile" ? `@${id}` : id ? `${kind.charAt(0).toUpperCase() + kind.slice(1)} · ${id}` : "IND RIL";

  useSEO({
    title: `${label} | IND RIL`,
    description: "Open this on IND RIL — India's Own Social Media Platform. Available on iOS and Android.",
    path,
  });

  // On mount, mobile → store (Universal / App Links have already had a chance
  // to intercept before this JS ran; if we're here, the app isn't installed).
  useEffect(() => {
    if (isBot || platform === "desktop") return;
    if (platform === "android") {
      // Force Google Play Store app (not OEM stores like Vivo/Oppo/Realme/Xiaomi).
      window.location.replace(buildPlayIntent());
      return;
    }
    if (platform === "ios" && APP_LINKS.appStore) {
      window.location.replace(APP_LINKS.appStore);
    }
  }, [platform, isBot]);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Header />
      <main className="mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center px-6 pt-28 pb-16 text-center" data-testid="smart-redirect">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-white/70">
            <Sparkles className="h-3 w-3 text-indril-saffron" /> Open in IND RIL
          </span>
          <h1 className="mt-6 font-display text-3xl font-semibold tracking-tight sm:text-4xl" data-testid="smart-redirect-title">{label}</h1>
          <p className="mx-auto mt-4 max-w-md text-white/60">
            {platform === "ios" && "Taking you to the App Store…"}
            {platform === "android" && "Taking you to Google Play…"}
            {(platform === "desktop" || isBot) && "This is an IND RIL content link. Download the app to view it."}
          </p>

          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <a href={APP_LINKS.appStore} target="_blank" rel="noopener noreferrer" data-testid="smart-app-store"
              className="inline-flex items-center gap-3 rounded-full bg-white px-6 py-3.5 text-zinc-950 shadow-[0_8px_40px_-10px_rgba(255,255,255,0.4)]">
              <Apple className="h-6 w-6" fill="currentColor" strokeWidth={0} />
              <span className="leading-none">
                <span className="block text-[10px] uppercase tracking-widest opacity-70">Download on the</span>
                <span className="block font-display text-lg font-semibold">App Store</span>
              </span>
            </a>
            <a href={APP_LINKS.googlePlay} target="_blank" rel="noopener noreferrer" data-testid="smart-google-play"
              className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-6 py-3.5 text-white backdrop-blur-xl">
              <Play className="h-6 w-6 text-indril-saffron" fill="currentColor" strokeWidth={0} />
              <span className="leading-none">
                <span className="block text-[10px] uppercase tracking-widest opacity-70">Get it on</span>
                <span className="block font-display text-lg font-semibold">Google Play</span>
              </span>
            </a>
          </div>

          <Link to="/" className="mt-10 inline-flex items-center gap-2 text-sm text-white/40 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to indril.in
          </Link>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
